import { useContext, useEffect, useRef, useState } from 'react';
import { CheckIcon, EditIcon } from 'assets/icons';
import { NotificationType } from 'common/enums';
import { appActions, AppContext } from 'context/app-context';
import { classnames } from 'helpers/utils';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import styles from './memory-title.module.scss';

interface MemoryTitleProps {
  content: AlbumContentSerialized | undefined;
  // Whether the title can be renamed inline (owner only).
  editable?: boolean;
  // Called after a successful rename so the parent can patch its state.
  onRenamed?: (contentId: number, name: string) => void;
  // 'header': centered on one line in the sub-header.
  // 'page': the phone screen's title, left-aligned and wrapping, as in the
  // mobile app.
  variant?: 'header' | 'page';
}

// The selected memory's name; for people who can edit, a button that turns
// into a rename field. Enter saves, Escape discards; leaving the field doesn't.
export const MemoryTitle = ({
  content,
  editable = false,
  onRenamed,
  variant = 'header',
}: MemoryTitleProps) => {
  const { dispatch } = useContext(AppContext);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(content?.name ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset the editor whenever the selected memory changes.
  useEffect(() => {
    setIsEditing(false);
    setDraft(content?.name ?? '');
  }, [content?.id, content?.name]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const notify = (title: string, type: NotificationType) =>
    dispatch({
      type: appActions.NOTIFICATION,
      notification: new NotificationObject({ show: true, title, type }),
    });

  const save = async () => {
    const trimmed = draft.trim();
    setIsEditing(false);
    if (!content || !trimmed || trimmed === (content.name ?? '')) {
      setDraft(content?.name ?? '');
      return;
    }
    try {
      await VaultsController.updateContentMetadata(content.id, {
        name: trimmed,
      });
      onRenamed?.(content.id, trimmed);
      notify('Memory name updated', NotificationType.Success);
    } catch (error) {
      notify('The name could not be updated', NotificationType.Error);
      setDraft(content.name ?? '');
    }
  };

  const cancel = () => {
    setIsEditing(false);
    setDraft(content?.name ?? '');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };

  // Keep focus on the input when a button is pressed so the click registers
  // before any blur handling.
  const keepInputFocus = (e: React.MouseEvent) => e.preventDefault();

  const startEditing = () => {
    if (!editable || !content) return;
    setDraft(content.name ?? '');
    setIsEditing(true);
  };

  const page = variant === 'page';

  if (isEditing) {
    return (
      <div className={classnames(styles.editRow, { [styles.page]: page })}>
        <input
          ref={inputRef}
          type="text"
          className={styles.input}
          value={draft}
          placeholder="Untitled"
          aria-label="Memory name"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <div className={styles.editActions}>
          <button
            type="button"
            className={styles.discardButton}
            onMouseDown={keepInputFocus}
            onClick={cancel}
          >
            Discard
          </button>
          <button
            type="button"
            className={styles.saveButton}
            onMouseDown={keepInputFocus}
            onClick={save}
          >
            <CheckIcon className={styles.saveIcon} />
            Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={classnames(styles.titleButton, { [styles.page]: page })}
      onClick={startEditing}
      disabled={!editable || !content}
    >
      <span className={styles.title}>{content?.name || 'Untitled'}</span>
      {editable && content && <EditIcon className={styles.editIcon} />}
    </button>
  );
};
